import { globalApiService } from '../globalApiSlice';
import type { StoreBaseResponse } from '@/types/types';
import { createCustomError, createCustomResponse } from '@/helpers/utils';
import { RolesService } from '@/services/masters/roles/rolesService';
import type { AddUpdateRoleRequestType } from '@/modules/masters/roles/components/NewRolePopup';
import { UserService } from '@/services/masters/user/userService';
import type { NullableUpdateSelfRequestType } from '@/types/authresponse';
import type { NullableAddUserPayload } from '@/types/userlist';

const rolesService = new RolesService();
const userService = new UserService();

const usersApi = globalApiService.injectEndpoints({
  endpoints: (build) => ({
    addRole: build.mutation<StoreBaseResponse<null>, AddUpdateRoleRequestType>({
      queryFn: async (data) => {
        try {
          const response = await rolesService.addNewRole(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    updateRole: build.mutation<
      StoreBaseResponse<null>,
      AddUpdateRoleRequestType
    >({
      queryFn: async (data) => {
        try {
          const response = await rolesService.updateRole(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    updateProfilePicForSelf: build.mutation<
      StoreBaseResponse<string>,
      FormData
    >({
      queryFn: async (data) => {
        try {
          const response = await userService.uploadProfilePicForSelf(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    updateProfileInfoForSelf: build.mutation<
      StoreBaseResponse<null>,
      NullableUpdateSelfRequestType
    >({
      queryFn: async (data) => {
        try {
          const response = await userService.updateProfileInfoForSelf(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    addUpdateUser: build.mutation<
      StoreBaseResponse<null>,
      {
        body: NullableAddUserPayload;
        awsBody: { email: string; password: string } | null;
        id: number | undefined;
      }
    >({
      queryFn: async ({ awsBody, body, id }) => {
        try {
          const isEditing = !!id;
          if (!isEditing && awsBody) {
            // code block to run only when adding new user
            const aws_response = await userService.addUserToAWS(awsBody);
            const aws_user_id = aws_response?.data?.userId;
            if (aws_response.error) {
              return createCustomError(aws_response);
            }
            if (aws_user_id) {
              body.aws_user_id = aws_user_id;
            }
          }
          const response = await userService.addUser(body, id);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useAddRoleMutation,
  useUpdateRoleMutation,
  useUpdateProfilePicForSelfMutation,
  useUpdateProfileInfoForSelfMutation,
  useAddUpdateUserMutation,
} = usersApi;
